import { AlertIcon } from '@approvalmax/ui';
import { Alert, Collapse, Divider, Flex, Spacing, Text } from '@approvalmax/ui/src/components';
import { compareHelpers } from '@approvalmax/utils';
import { asMutable } from 'modules/immutable';
import { Fragment, memo } from 'react';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { getColumnGroups } from '../../../../config/columnGroups';
import ColumnKind from '../../../../config/standard/columnKind';
import { FilterType } from '../../../../data/filters';
import { RequestAuditReportState } from '../../../../types/domain';
import { ReportConfigColumn } from '../../../../types/ReportConfigColumn';
import { FilterComponent } from '../FilterComponent/FilterComponent';
import { useSectionFilters } from './SectionFilters.hooks';
import { messages } from './SectionFilters.messages';
import { SectionFiltersProps } from './SectionFilters.types';

export const SectionFilters = memo<SectionFiltersProps>((props) => {
    const { newReport, reportConfig, onReportConfigChange } = props;

    const company = useCompanyBasedOnUrl();

    const {
        integration,
        integrationName,
        hasActiveIntegration,
        hasConnectedAirwallex,
        expandedColumns,
        companyTeam,
        handleChangeFilter,
    } = useSectionFilters({ company, reportConfig, onReportConfigChange, newReport });

    const expandedColumns2 = expandedColumns.filter(
        // we don't show TemplateId column on the list
        // we don't show columns that we can't filter by
        (expandedColumn) =>
            expandedColumn.column.kind !== ColumnKind.TemplateId &&
            expandedColumn.columnDefinition.filterType !== FilterType.None
    );

    let hasAirwallexRequiredColumn = false;

    const sections = getColumnGroups(company)
        .map((columnGroup) => {
            const columns = expandedColumns2
                .filter((expandedColumn) => expandedColumn.columnDefinition.group === columnGroup.id)
                .map((expandedColumn) => expandedColumn.column);

            if (columns.length === 0) {
                return null;
            }

            const columnsSorted = asMutable(columns).sort(
                compareHelpers.comparatorFor<ReportConfigColumn>(compareHelpers.stringComparator2AscI, 'name')
            );

            // TODO: Try to move to separated component
            return (
                <Collapse
                    localStorageKey={`filters-${columnGroup.id}`}
                    bordered
                    radius='small'
                    spacing='0 24 16 24'
                    name={columnGroup.text}
                    key={columnGroup.id}
                >
                    <Flex direction='column' spacing='12 0'>
                        {columnsSorted.map((column, columnIndex) => {
                            const isLastColumn = columnIndex === columnsSorted.length - 1;

                            let expandedCol = expandedColumns.find(
                                (expandedColumn) => expandedColumn.column === column
                            )!;

                            if (expandedCol.options && expandedCol.column.kind === ColumnKind.AuditReportStatus) {
                                const modifiedExpandedCol = { ...expandedCol };

                                modifiedExpandedCol.options = modifiedExpandedCol.options?.filter(
                                    (option) => option.id !== RequestAuditReportState.Reserved
                                );
                                expandedCol = modifiedExpandedCol;
                            }

                            const isAirwallexRequired =
                                expandedCol.columnDefinition.requiresConnectedServices?.airwallex;

                            if (isAirwallexRequired) {
                                hasAirwallexRequiredColumn = true;
                            }

                            const disabledXero =
                                expandedCol.columnDefinition.requiresConnectedIntegration && !hasActiveIntegration;
                            const disabledAirwallex = isAirwallexRequired && !hasConnectedAirwallex;
                            const disabled = disabledXero || disabledAirwallex;

                            return (
                                <Fragment key={column.id}>
                                    <Flex
                                        alignItems={disabled ? 'center' : 'end'}
                                        wrap={false}
                                        spacing='8'
                                        data-qa='filter-row'
                                    >
                                        <Flex direction='column' width={200} shrink={0} grow={0} spacing='0'>
                                            <Spacing height={8} />

                                            <Text font='label' fontSize='small' color='midnight70' title={column.title}>
                                                {column.name}
                                            </Text>

                                            <Spacing height={8} />
                                        </Flex>

                                        <Flex block grow={1} wrap={false}>
                                            {disabled ? (
                                                <Text font='body' fontSize='small' color='midnight60'>
                                                    {messages.disabledFieldText({
                                                        integrationName: disabledXero ? integrationName : 'Airwallex',
                                                    })}
                                                </Text>
                                            ) : (
                                                <FilterComponent
                                                    column={column}
                                                    field={expandedCol.field}
                                                    companyTeam={companyTeam}
                                                    options={expandedCol.options || []}
                                                    onChange={handleChangeFilter}
                                                />
                                            )}
                                        </Flex>
                                    </Flex>

                                    {!isLastColumn && <Divider />}
                                </Fragment>
                            );
                        })}
                    </Flex>
                </Collapse>
            );
        })
        .filter(Boolean);

    const showWarningIntegration = integration && !hasActiveIntegration;
    const showWarningAirwallex = hasAirwallexRequiredColumn && !hasConnectedAirwallex;

    return (
        <Flex direction='column'>
            {(showWarningIntegration || showWarningAirwallex) && (
                <Alert startIcon={<AlertIcon />} color='midnight20'>
                    {messages.disconnectionWarningText({
                        integrationName: showWarningIntegration ? integrationName : 'Airwallex',
                    })}
                </Alert>
            )}

            {sections}
        </Flex>
    );
});

SectionFilters.displayName = 'SectionFilters';
